import { observer } from "mobx-react-lite";
import { useParams, useSearchParams } from "react-router-dom";
import ESGReportWizardHeader from "../components/reports/reportWizard/ESGReportWizardHeader";
import { useEffect } from "react";
import {
  useESGCollaborationStore,
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
  useESGReportBuilderStore,
  useESGReportWorkflowStore,
} from "../hooks/useESGReportStore";
import { NotFoundPage } from "@/components/common";
import ReportSettingsStep from "../components/reports/reportWizard/ReportSettingsStep";
import DisclosureSelectionStep from "../components/reports/reportWizard/DisclosureSelectionStep";
import { readStoredOrg } from "@/features/organization/stores/organization.store";
import DataEntryStep from "../components/reports/reportWizard/DataEntryStep";
import ReviewStep from "../components/reports/reportWizard/ReviewStep";
import { frameworkInfos } from "@/lib/constants/frameworks";
import { useMemo } from "react";

const ESGReportPage = observer(() => {
  const { framework, stepId } = useParams();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("reportId");

  const { goToStep, steps, currentStep, resetWorkflow } =
    useESGReportWorkflowStore();
  const {
    isNew,
    setFramework,
    setOrganization,
    setReportId,
    setReportName,
    loadReport,
    resetBuilder,
  } = useESGReportBuilderStore();
  const {
    loadDisclosuresForFramework,
    loadMetricMappings,
    loadCompanyCustomDisclosures,
    resetDisclosure,
  } = useESGDisclosureSelectionStore();
  const { resetDataEntry } = useESGDataEntryStore();
  const { resetCollaboration } = useESGCollaborationStore();
  const { id, name } = readStoredOrg();

  const frameworkInfo = useMemo(() => {
    return (
      frameworkInfos.find(({ key }) => key === framework) || frameworkInfos[0]
    );
  }, [framework]);

  useEffect(() => {
    resetWorkflow();
    resetBuilder();
    resetDisclosure();
    resetDataEntry();
    resetCollaboration();
  }, []);

  useEffect(() => {
    if (framework) setFramework(framework);
    if (stepId) goToStep(Number(stepId));
    if (reportId) setReportId(reportId);
  }, [framework, stepId, reportId]);

  useEffect(() => {
    setOrganization(id, name);
  }, [id, name]);

  useEffect(() => {
    loadDisclosuresForFramework(framework);
    loadCompanyCustomDisclosures();
    if (framework === "UNIVERSAL") {
      loadMetricMappings();
    }
    if (reportId && reportId !== "new") {
      loadReport(reportId);
    }
    if (isNew) setReportName(frameworkInfo.value);
  }, [framework, reportId, isNew]);

  if (!stepId || stepId > steps.length) return <NotFoundPage />;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ReportSettingsStep frameworkInfo={frameworkInfo} />;
      case 2:
        return <DisclosureSelectionStep />;
      case 3:
        return <DataEntryStep />;
      case 4:
        return <ReviewStep />;
      default:
        return <ReportSettingsStep frameworkInfo={frameworkInfo} />;
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-6 overflow-hidden">
      <ESGReportWizardHeader />
      <section className="flex-1 min-h-0 flex flex-col space-y-2 overflow-hidden">
        {renderStep()}
      </section>
    </div>
  );
});

export default ESGReportPage;
